import _ from "lodash";

import { OptionType } from "../../../../features/manageHomepage/ManageHomePageTypes";
import {
  convertValuesToConfigNew,
  doFinalConfigSubmit,
  fetchMultipleUIConfigDataNew,
  getStudioConfigMap,
} from "../../../../services/uiConfigService";
import { ServiceHandler } from "../Factory/ServiceFactory";
import * as Sentry from "@sentry/react";
import { configurationAttributeType } from "../../../../types";

export class UiConfigService implements ServiceHandler {
  initialData: any;
  defaultValues: any = {};
  dependsOnRole: boolean = false;
  savedRole: any;
  key: string = "";
  domainMapping: Record<string, string[]> = {};
  initialConfigMap: Record<string, any> = {};
  updatedConfigMap: Record<string, any> = {};
  pwaRequired?: boolean | undefined;
  requiresStudioSavePopup?: boolean | undefined;

  constructor() {
    this.dependsOnRole = true;
    this.key = "name";
    this.requiresStudioSavePopup = true;
    this.pwaRequired = true;
    this.initialData = {};
  }

  async setDefaultValues(domainMapping: any): Promise<any> {
    this.domainMapping = domainMapping;
    try {
      const stringMap: string[] = [];
      for (let key in this.domainMapping) {
        for (let objectkey in this.domainMapping[key]) {
          stringMap.push(this.domainMapping[key][objectkey]);
        }
      }
      const defaultVal = await fetchMultipleUIConfigDataNew(stringMap);
      this.defaultValues = defaultVal;
      return defaultVal;
    } catch (e) {
      throw Error("Could not load default Values");
    }
  }

  async getData(savedRole?: OptionType): Promise<any> {
    try {
      console.log("getting domain", this.domainMapping, this.defaultValues);
      const studioConfigMap = await getStudioConfigMap(this.domainMapping);
      this.initialConfigMap = studioConfigMap;
      const val = this.setupConfigState(savedRole!);
      return val;
    } catch (error) {
      console.error("Error in fetching the config", error);
      throw new Error("CONFIG_FETCH_FAILED");
    }
  }

  setupConfigState = (role: OptionType) => {
    this.savedRole = role;

    if (!this.initialConfigMap) {
      return;
    }

    let totalArray: any[] = [];

    for (let key in this.domainMapping) {
      for (let objectkey in this.domainMapping[key]) {
        const appConfig =
          this.initialConfigMap[key]?.find(
            (config: any) =>
              config?.domainType ===
              `${role.id}_${this.domainMapping[key][objectkey]}`
          ) ?? {};

        const tempAppConfigArray = appConfig?.["domainValues"] ?? [];

        const appConfigArray = tempAppConfigArray.map((item: any) => {
          item.domainType = this.domainMapping[key][objectkey];
          if (item.isValueUpdated) delete item.isValueUpdated;
          return item;
        });

        const newAppConfigValue = structuredClone(this.defaultValues);

        for (const key in newAppConfigValue) {
          if (newAppConfigValue?.hasOwnProperty(key)) {
            const matchingConfig = appConfigArray.find(
              (cfg: Record<string, any>) => cfg.name === key
            );
            if (matchingConfig) {
              newAppConfigValue[key].value = matchingConfig?.value;
            }
          }
        }

        totalArray = [...totalArray, ...appConfigArray];
      }
    }

    const obj = totalArray.reduce((acc: any, item: any) => {
      acc[item.name] = item;
      return acc;
    }, {});

    this.initialData = { ...structuredClone(obj) };
    return this.initialData;
  };

  SaveData(value: any): any {
    try {
      const val = { ...value };

      for (let key in val) {
        const obj1 = val[key];
        const obj2 = this.initialData[key];
        if (obj1?.isValueUpdated) {
          if (_.isEqual(obj1?.value, obj2?.value)) delete obj1.isValueUpdated;
        }
      }

      const resultantValues = { ...val };
      const {
        result: convertedVals,
        failedValidations,
        missingFromGlobal,
      } = convertValuesToConfigNew(
        resultantValues,
        Object.values(val),
        this.defaultValues
      );

      if (missingFromGlobal.length > 0) {
        const missingMsg = missingFromGlobal.join(", ");
        throw new Error(
          `Some errors occurred while saving data.\n\n${missingMsg}`
        );
      }

      if (failedValidations.length > 0) {
        const failedMsg = failedValidations.join(", ");
        throw new Error(
          `Some errors occurred while saving data.\n\n${failedMsg}`
        );
      }

      this.updatedConfigMap = structuredClone(this.initialConfigMap);

      for (let key in this.domainMapping) {
        for (let objectkey in this.domainMapping[key]) {
          const configList = this.updatedConfigMap?.[key];

          if (Array.isArray(configList)) {
            const Obj = configList.find(
              (obj: any) =>
                obj?.domainType ===
                `${this.savedRole.id}_${this.domainMapping[key][objectkey]}`
            );
            const domainSpecificConvertedVals = convertedVals.filter(
              (item) => item.domainType === this.domainMapping[key][objectkey]
            );
            if (Obj) {
              Obj.domainValues = domainSpecificConvertedVals;
            } else {
              configList.push({
                domainType: `${this.savedRole.id}_${this.domainMapping[key][objectkey]}`,
                domainValues: domainSpecificConvertedVals,
              });
            }
          }
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      console.log("err===", err);
      if (err instanceof Error) throw err;
    }
  }

  getDescription(config: string) {}

  getPrifillValue(key: string): any {
    // return this.currentData?.[key]?.value;
  }

  updateState(
    key: string,
    newValue: any,
    values: any,
    extendedAttributes?: any
  ): void {
    const updatedValues = {
      ...values,
      [key]: {
        ...values?.[key],
        value: newValue,
        isValueUpdated: true,
      },
    };
    return updatedValues;
  }

  async handlePreview(
    setPlayGroundConfiguration: any,
    setKeyValue: any,
    keyValue: any,
    setCleanSyncDataLoaderIds: any,
    subFeature: any,
    values: any
  ): Promise<any> {
    const getConvertedVals = async ({
      eventTriggeredByPreview,
    }: { eventTriggeredByPreview?: boolean } = {}) => {
      try {
        const mainValue: configurationAttributeType[] = Object.values(values);
        const val = { ...values };
        let loaders: string[] = [];

        for (let key in val) {
          const obj1 = val[key];
          const obj2 = this.initialData[key];
          if (obj1?.isValueUpdated) {
            if (_.isEqual(obj1?.value, obj2?.value)) delete obj1.isValueUpdated;
            else {
              const loader =
                subFeature?.configuration?.find((obj: any) => obj.name === key)
                  ?.loaders ?? [];
              loader.map((load: any) => {
                if (!loaders.includes(load)) loaders.push(load);
              });
            }
          }
        }

        setCleanSyncDataLoaderIds((prev: string[]) => {
          const filtered = loaders.filter((l) =>
            prev.find((prevLoader) => prevLoader === l)
          );
          if (filtered) return [...prev, ...filtered];
          return prev;
        });

        const resultantValues = { ...val };
        const { result, failedValidations, missingFromGlobal } =
          convertValuesToConfigNew(
            resultantValues,
            Object.values(val),
            this.defaultValues
          );

        if (missingFromGlobal.length > 0) {
          const missingMsg = missingFromGlobal.join(", ");
          throw Error(
            `Some errors occurred while saving data.\n\n${missingMsg}`
          );
        }
        if (failedValidations.length > 0) {
          const failedMsg = failedValidations.join(", ");
          throw new Error(
            `Some errors occurred while saving data.\n\n${failedMsg}`
          );
        }
        return result;
      } catch (error) {
        throw Error("error in saving");
      }
    };

    try {
      const convertedVals: configurationAttributeType[] =
        (await getConvertedVals()) ?? [];

      setPlayGroundConfiguration(convertedVals);

      const res: any = await doFinalConfigSubmit(
        "playground_config",
        convertedVals
      );
      if (res["status"] === 200) {
        setKeyValue(keyValue + 1);
      }
    } catch (error) {
      console.error("Error in onTriggerConfigChanges:", error);
    }
  }
}
